import { useEffect, useReducer, useState } from 'react';
import { guides, bodyLoaders, type GuideCategory, type GuideMeta } from 'virtual:guides';

export type { GuideCategory, GuideMeta };

export const GUIDES: GuideMeta[] = guides;

export const GUIDE_CATEGORY_LABELS: Record<GuideCategory, string> = {
  'how-to': 'How-to',
  guide: 'Guide',
  checklist: 'Checklist',
  explainer: 'Explainer',
};

export function getGuideBySlug(slug: string): GuideMeta | undefined {
  return GUIDES.find((g) => g.slug === slug);
}

// Guide bodies are lazy chunks. Rendering is synchronous (renderToString at prerender time,
// hydrateRoot on first load), so both entries await preloadForPath() before rendering; client-side
// navigation to a guide loads its body through useGuideBody().
const loadedBodies = new Map<string, string>();

async function preloadGuide(slug: string): Promise<void> {
  const load = bodyLoaders[slug];
  if (!load || loadedBodies.has(slug)) return;
  loadedBodies.set(slug, (await load()).default);
}

const GUIDE_PATH = /^\/guides\/([^/]+)\/?$/;

export async function preloadForPath(pathname: string): Promise<void> {
  const slug = GUIDE_PATH.exec(pathname)?.[1];
  if (slug) await preloadGuide(slug);
}

export type GuideBodyState =
  | { status: 'loading' }
  | { status: 'loaded'; body: string }
  | { status: 'failed' };

/** The guide's markdown body: loaded, still loading, or failed (e.g. its chunk was deployed away). */
export function useGuideBody(slug: string): GuideBodyState {
  const [failedSlug, setFailedSlug] = useState<string | null>(null);
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const body = loadedBodies.get(slug);

  useEffect(() => {
    if (body !== undefined) return;
    let active = true;
    preloadGuide(slug).then(
      () => active && rerender(),
      () => active && setFailedSlug(slug),
    );
    return () => {
      active = false;
    };
  }, [slug, body]);

  if (body !== undefined) return { status: 'loaded', body };
  return failedSlug === slug ? { status: 'failed' } : { status: 'loading' };
}
